import { useCallback, useEffect, useState } from 'react';
import PaperViewer from '../components/PaperViewer';
import PaperCalendar from '../components/PaperCalendar';
import MonthlyPaperPicker from '../components/MonthlyPaperPicker';
import { useLang } from '../context/LanguageContext';
import { useAuth, hasFullAccess } from '../context/AuthContext';
import { useSubscriptionGate } from '../context/SubscriptionGateContext';
import { useNewsData } from '../context/NewsDataContext';
import { goNativeTab } from './NativeTabContext';
import {
  ensureDailyPaperAccess,
  ensureMonthlyPaperAccess,
  getCachedMonthlyAccess,
  warmDailyPaper,
  warmMonthlyPaper,
  getPdfBytes,
  putPdfBuffer,
  clearDailyPaperAccess,
  OPEN_DAILY_PAPER_EVENT,
  OPEN_MONTHLY_PAPER_EVENT,
} from '../utils/paperCache';
import { loadOfflinePdf, saveOfflinePdf, offlineBlobUrl, listOfflineIds } from '../utils/offlinePapers';
import { isNativeApp } from '../utils/platform.js';
import { markEditionSeen } from '../utils/editionAlerts';
import { API_URL as API, SITE_URL } from '../config/env.js';
import MagazineCover from './MagazineCover';
import { shareLink } from '../utils/nativeShare';
import './NativePaperScreen.css';

const LOCALE_MAP = { te: 'te-IN', en: 'en-IN', ta: 'ta-IN', kn: 'kn-IN' };

function formatMonthLabel(monthKey, lang) {
  const [y, m] = monthKey.split('-');
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(LOCALE_MAP[lang] || 'te-IN', {
    month: 'short',
    year: 'numeric',
  });
}

export default function NativePaperScreen({ active, openEditionDate = null }) {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const { homeFeed } = useNewsData();
  const { openSubscribe } = useSubscriptionGate();
  const fullAccess = hasFullAccess(user);

  const [mode, setMode] = useState('daily');
  const [showPicker, setShowPicker] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLabel, setPdfLabel] = useState('');
  const [previewMaxPages, setPreviewMaxPages] = useState(null);
  const [resolvingPaper, setResolvingPaper] = useState(false);
  const [paperError, setPaperError] = useState('');
  const [latestDaily, setLatestDaily] = useState(null);
  const [months, setMonths] = useState([]);
  const [latestMonthly, setLatestMonthly] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [offlineSaved, setOfflineSaved] = useState(false);

  const isDaily = mode === 'daily';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isNativeApp()) return;
      if (isDaily) {
        const key = selectedDate || latestDaily;
        if (!key) {
          setOfflineSaved(false);
          return;
        }
        const ids = await listOfflineIds('daily');
        if (!cancelled) setOfflineSaved(ids.includes(key));
      } else {
        const key = selectedMonth || latestMonthly;
        if (!key) {
          setOfflineSaved(false);
          return;
        }
        const ids = await listOfflineIds('monthly');
        if (!cancelled) setOfflineSaved(ids.includes(key));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isDaily, selectedDate, selectedMonth, latestDaily, latestMonthly, pdfUrl]);

  useEffect(() => {
    if (homeFeed.latestPaperDate) setLatestDaily(homeFeed.latestPaperDate);
  }, [homeFeed.latestPaperDate]);

  useEffect(() => {
    fetch(`${API}/api/papers`)
      .then((r) => r.json())
      .then((d) => {
        if (d.dates?.length) setLatestDaily((prev) => prev || d.dates[0]);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch(`${API}/api/monthly-papers`)
      .then((r) => r.json())
      .then((d) => {
        if (d.months?.length) {
          setMonths(d.months);
          setLatestMonthly(d.months[0]);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!latestDaily) return;
    warmDailyPaper(API, user?.token, latestDaily, { preview: !user || !fullAccess });
  }, [user?.token, latestDaily, fullAccess, user]);

  useEffect(() => {
    if (!user?.token || !latestMonthly || !fullAccess) return;
    warmMonthlyPaper(API, user.token, latestMonthly);
  }, [user?.token, latestMonthly, fullAccess]);

  const formatDailyLabel = useCallback(
    (dateKey) =>
      new Date(`${dateKey}T00:00:00`).toLocaleDateString(LOCALE_MAP[lang] || 'te-IN', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }),
    [lang]
  );

  const formatDateBox = (dateKey) => {
    if (!dateKey) return t.nativeToday;
    return new Date(`${dateKey}T00:00:00`).toLocaleDateString(LOCALE_MAP[lang] || 'en-IN', {
      day: 'numeric',
      month: 'short',
    });
  };

  const openDailyPaper = useCallback(
    async (dateKey, { preview = false } = {}) => {
      const isLatest = dateKey === latestDaily;
      const usePreview = preview || (!fullAccess && isLatest) || (!user && isLatest);
      if (!user && !isLatest) {
        window.location.href = `/login?next=${encodeURIComponent(window.location.pathname)}`;
        return;
      }

      setMode('daily');
      setSelectedDate(dateKey);
      setPaperError('');
      setPdfLabel(formatDailyLabel(dateKey));
      setResolvingPaper(true);
      setPdfUrl(null);
      setPreviewMaxPages(null);

      const openFromOffline = async () => {
        if (!isNativeApp() || usePreview) return false;
        const bytes = await loadOfflinePdf('daily', dateKey);
        if (!bytes) return false;
        const url = offlineBlobUrl(bytes);
        putPdfBuffer(url, bytes);
        setPreviewMaxPages(null);
        setPdfUrl(url);
        markEditionSeen(dateKey);
        return true;
      };

      try {
        let access = await ensureDailyPaperAccess(API, user?.token, dateKey, { preview: usePreview });
        try {
          const bytes = await getPdfBytes(access.pdfUrl);
          if (isNativeApp() && !usePreview) {
            void saveOfflinePdf('daily', dateKey, bytes);
          }
        } catch {
          clearDailyPaperAccess(dateKey, usePreview);
          access = await ensureDailyPaperAccess(API, user?.token, dateKey, {
            preview: usePreview,
            force: true,
          });
          const bytes = await getPdfBytes(access.pdfUrl);
          if (isNativeApp() && !usePreview) {
            void saveOfflinePdf('daily', dateKey, bytes);
          }
        }
        setPreviewMaxPages(access.maxPages);
        setPdfUrl(access.pdfUrl);
        markEditionSeen(dateKey);
      } catch (err) {
        if (await openFromOffline()) return;
        if (err?.code === 'LOGIN') {
          window.location.href = `/login?next=${encodeURIComponent(window.location.pathname)}`;
          return;
        }
        if (err?.code === 'SUBSCRIBE' || err?.status === 403) {
          openSubscribe();
          return;
        }
        setPaperError(t.paperNotAvailable);
      } finally {
        setResolvingPaper(false);
      }
    },
    [formatDailyLabel, t.paperNotAvailable, user?.token, user, fullAccess, latestDaily, openSubscribe]
  );

  const openMonthlyPaper = useCallback(
    async (monthKey) => {
      if (!user?.token) {
        window.location.href = `/login?next=${encodeURIComponent(window.location.pathname)}`;
        return;
      }
      setMode('monthly');
      setSelectedMonth(monthKey);
      const cached = getCachedMonthlyAccess(monthKey);
      setPaperError('');
      setPdfLabel(formatMonthLabel(monthKey, lang));

      const openFromOffline = async () => {
        if (!isNativeApp()) return false;
        const bytes = await loadOfflinePdf('monthly', monthKey);
        if (!bytes) return false;
        const url = offlineBlobUrl(bytes);
        putPdfBuffer(url, bytes);
        setPreviewMaxPages(null);
        setPdfUrl(url);
        return true;
      };

      if (cached) {
        setPdfUrl(cached.pdfUrl);
        setPreviewMaxPages(null);
        setResolvingPaper(false);
        getPdfBytes(cached.pdfUrl)
          .then((bytes) => {
            if (isNativeApp()) void saveOfflinePdf('monthly', monthKey, bytes);
          })
          .catch(() => {});
        return;
      }
      setResolvingPaper(true);
      setPdfUrl(null);
      setPreviewMaxPages(null);
      try {
        const access = await ensureMonthlyPaperAccess(API, user.token, monthKey);
        try {
          const bytes = await getPdfBytes(access.pdfUrl);
          if (isNativeApp()) void saveOfflinePdf('monthly', monthKey, bytes);
        } catch {
          /* still open via url */
        }
        setPdfUrl(access.pdfUrl);
      } catch (err) {
        if (await openFromOffline()) return;
        if (err?.code === 'SUBSCRIBE' || err?.status === 403) {
          openSubscribe();
          return;
        }
        setPaperError(t.paperNotAvailable);
      } finally {
        setResolvingPaper(false);
      }
    },
    [lang, t.paperNotAvailable, user?.token, openSubscribe]
  );

  useEffect(() => {
    if (!active) return;
    if (openEditionDate && latestDaily) {
      openDailyPaper(openEditionDate);
    }
  }, [active, openEditionDate, latestDaily, openDailyPaper]);

  useEffect(() => {
    const onOpenDaily = (event) => {
      const dateKey = event.detail?.dateKey || latestDaily;
      if (!dateKey) return;
      openDailyPaper(dateKey, { preview: !fullAccess });
    };
    window.addEventListener(OPEN_DAILY_PAPER_EVENT, onOpenDaily);
    return () => window.removeEventListener(OPEN_DAILY_PAPER_EVENT, onOpenDaily);
  }, [openDailyPaper, fullAccess, latestDaily]);

  useEffect(() => {
    const onOpenMonthly = (event) => {
      const monthKey = event.detail?.monthKey;
      if (!monthKey) return;
      setMode('monthly');
      setSelectedMonth(monthKey);
      if (event.detail?.read) openMonthlyPaper(monthKey);
      else {
        setPdfUrl(null);
        setResolvingPaper(false);
      }
    };
    window.addEventListener(OPEN_MONTHLY_PAPER_EVENT, onOpenMonthly);
    return () => window.removeEventListener(OPEN_MONTHLY_PAPER_EVENT, onOpenMonthly);
  }, [openMonthlyPaper]);

  useEffect(() => {
    if (!active || openEditionDate) return;
    if (!isDaily || !latestDaily || selectedDate) return;
    openDailyPaper(latestDaily);
  }, [active, openEditionDate, isDaily, latestDaily, selectedDate, openDailyPaper]);

  const pickNewspaper = () => {
    setMode('daily');
    const key = selectedDate || latestDaily;
    if (key) openDailyPaper(key);
  };

  const pickMagazine = () => {
    setMode('monthly');
    setPdfUrl(null);
    setPaperError('');
    setResolvingPaper(false);
  };

  const monthKey = selectedMonth || latestMonthly;
  const dateValue = isDaily
    ? formatDateBox(selectedDate || latestDaily)
    : monthKey
      ? formatMonthLabel(monthKey, lang)
      : t.monthlyEdition;

  return (
    <div className="rk-epaper">
      <div className="rk-epaper__rail">
        <div className="rk-epaper__seg" role="tablist">
          <button type="button" role="tab" aria-selected={isDaily} className={isDaily ? 'is-on' : ''} onClick={pickNewspaper}>
            {t.nativeToday}
          </button>
          <button type="button" role="tab" aria-selected={!isDaily} className={!isDaily ? 'is-on' : ''} onClick={pickMagazine}>
            {lang === 'en' ? 'Magazines' : t.monthlyHeadline}
          </button>
        </div>
        <button type="button" className="rk-epaper__date" onClick={() => setShowPicker(true)}>
          {dateValue}
          {offlineSaved ? <span className="rk-epaper__offline-dot" title={t.nativeOfflineHint} aria-label={t.nativeOfflineHint} /> : null}
        </button>
        {pdfUrl ? (
          <button
            type="button"
            className="rk-epaper__share"
            aria-label={t.shareWhatsAppTitle || t.nativeShareApp}
            onClick={() => {
              const url = SITE_URL || 'https://repatikosam.com';
              void shareLink({
                title: t.brandName,
                text: `${t.shareMessage}${pdfLabel ? ` — ${pdfLabel}` : ''}`,
                url,
              });
            }}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" />
            </svg>
          </button>
        ) : null}
      </div>

      {paperError && (
        <p className="rk-epaper__err" role="alert">
          {paperError}
        </p>
      )}

      <div className="rk-epaper__stage">
        {active && isDaily && (
          <PaperViewer
            embedded
            onClose={() => goNativeTab('home')}
            pdfPath={pdfUrl}
            waitingForPath={resolvingPaper}
            dateLabel={pdfLabel}
            resumeKey={selectedDate || latestDaily}
            maxPages={previewMaxPages}
            onPreviewSubscribe={openSubscribe}
          />
        )}
        {active && !isDaily && !pdfUrl && (
          <div className="rk-mags">
            <p className="rk-mags__title">{t.monthlyHeadline}</p>
            <div className="rk-mags__grid">
              {(months.length ? months : [latestMonthly].filter(Boolean)).slice(0, 8).map((key) => (
                <button key={key} type="button" className="rk-mags__item" onClick={() => openMonthlyPaper(key)}>
                  <span className="rk-mags__cover">
                    <MagazineCover monthKey={key} />
                  </span>
                  <strong>{formatMonthLabel(key, lang)}</strong>
                </button>
              ))}
            </div>
          </div>
        )}
        {active && !isDaily && pdfUrl && (
          <PaperViewer
            embedded
            onClose={() => goNativeTab('home')}
            pdfPath={pdfUrl}
            waitingForPath={resolvingPaper}
            dateLabel={pdfLabel}
            resumeKey={selectedMonth || latestMonthly}
            maxPages={previewMaxPages}
            onPreviewSubscribe={openSubscribe}
          />
        )}
      </div>

      {showPicker && isDaily && (
        <PaperCalendar
          onSelectDate={(dateKey) => {
            setShowPicker(false);
            openDailyPaper(dateKey);
          }}
          onClose={() => setShowPicker(false)}
        />
      )}
      {showPicker && !isDaily && (
        <MonthlyPaperPicker
          onSelectMonth={(monthKey) => {
            setShowPicker(false);
            openMonthlyPaper(monthKey);
          }}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}
